import { useEffect } from 'react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { TERMS_VERSION } from '../lib/legalVersions';

/**
 * Terms of Use for the Gamerhood S5 site. State Farm's corporate terms scope
 * themselves to statefarm.com only and do not cover vendor-operated activation
 * sites, so this page is the site-specific authority.
 *
 * VERSIONING: TERMS_VERSION is the single source in lib/legalVersions.ts — it's
 * both recorded against each user's termsAcceptedAt at registration (see
 * ConsentStep.tsx) and displayed here, so the stored consent value and the page
 * never drift. Bump it there when the terms change materially. TERMS_LAST_UPDATED
 * is display-only.
 */
export const TERMS_LAST_UPDATED = '2026-06-26';

const SUPPORT_CONTACT = 'privacy@infinitymarketing.com';

export default function TermsPage() {
  useEffect(() => {
    applyPageMeta(PAGE_META.terms);
  }, []);

  return (
    <article className="mx-auto max-w-3xl px-6 py-16 text-white/85">
      <header className="border-b border-white/15 pb-6">
        <h1 className="font-display text-3xl uppercase tracking-widest text-brand-redAccent">
          Terms of Use
        </h1>
        <p className="mt-3 text-sm text-white/60">
          Version {TERMS_VERSION} &middot; Last updated {TERMS_LAST_UPDATED}
        </p>
      </header>

      <RulesAuthorityNotice />

      <div className="mt-8 space-y-12 leading-relaxed">
        <Section id="acceptance" title="1. Acceptance of these terms">
          <p>
            These Terms only govern your use of the Site and the related game,{' '}
            <em>Jake's Neighborhood Swarm</em>. The Site is operated by
            Infinity Marketing Team, LLC ("Infinity Marketing," "we") on
            behalf of State Farm Mutual Automobile Insurance Company ("State
            Farm"). By accessing or using the Site you agree to these Terms.
            If you do not agree, do not use the Site.
          </p>
        </Section>

        <Section id="accounts" title="2. Accounts">
          <p>
            To play the game and enter the contest you create an account
            using your email address. You are responsible for maintaining the
            accuracy and confidentiality of your login credentials and for the
            security of your account. We use one-time email passcodes to verify
            access; you should not share those passcodes with anyone.
          </p>
          <p>
            You may delete your account at any time from the account
            settings page or by writing to{' '}
            <a
              href={`mailto:${SUPPORT_CONTACT}`}
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              {SUPPORT_CONTACT}
            </a>
            . We may suspend or terminate accounts that violate these Terms,
            the Official Rules, or applicable law. If you believe your account
            has been compromised, we encourage you to notify us promptly at the
            email address provided above.
          </p>
        </Section>

        <Section id="acceptable-use" title="3. Acceptable use">
          <p>
            Conduct relating to the contest itself — including entry limits,
            automated or bot play, and grounds for disqualification — is governed
            by the{' '}
            <a href="/rules" className="text-brand-redAccent underline-offset-2 hover:underline">
              Official Rules
            </a>{' '}
            and is not restated here. In addition, to protect the Site and other
            users, you agree not to:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              attempt to access, reverse engineer, or interfere with the Site,
              the game, or any of our systems, including the encrypted score
              submission path;
            </li>
            <li>
              probe, scan, or otherwise test the Site's security without our
              written permission;
            </li>
            <li>
              upload or transmit malicious code, or engage in any activity
              that could disrupt or damage the Site.
            </li>
          </ul>
        </Section>

        <Section id="intellectual-property" title="4. Intellectual property">
          <p>
            The Site, the game, the Gamerhood and State Farm names and
            logos, and all related content are owned by State Farm,
            Infinity Marketing, or our respective licensors and are
            protected by copyright, trademark, and other laws.
          </p>
          <p>
            We grant you a limited, non-exclusive, non-transferable,
            revocable license to use the Site and the game for personal,
            non-commercial purposes as permitted by these Terms. You may
            not copy, distribute, modify, create derivative works of, frame,
            or commercially exploit any part of the Site or the game
            without our prior written consent.
          </p>
          <p>
            If you submit feedback or suggestions about the Site or the
            game, you grant us a perpetual, worldwide, royalty-free license
            to use them for any purpose.
          </p>
        </Section>

        <Section id="third-party-content" title="5. Third-party content and links">
          <p>
            The Site contains links to third-party content, including pages
            on statefarm.com, infinitymarketing.com, YouTube, Twitch, and
            social media platforms. We do not control those services and
            are not responsible for their content or practices. Following a
            link from the Site is at your own risk and is subject to the
            terms of the third-party service.
          </p>
        </Section>

        {/*
          The three sections below restore the liability/disclaimer/dispute
          protections that the contest-only dedup had removed, re-scoped from
          "by participating / entering the Contest" to "by accessing or using
          the Site" so they bind every visitor — not just contest entrants.
          The wording tracks the Official Rules verbatim so the two documents
          cannot diverge; each deviation from the Rules text is a minimal scope
          swap or a gap-fill flagged inline with REVIEW: for legal sign-off.
        */}

        <Section id="disclaimers" title="6. Disclaimers">
          {/* Source: Official Rules §10(c) (as-is / implied warranties) + §14
              (responsibility for failures), re-scoped to the Site and game. */}
          <p>
            {/* REVIEW: §10(c) applies "as is" to the prize; here re-scoped to
                the Site and the game. "Contest Entities" carries the meaning
                given in the Official Rules. */}
            The Site and the game are provided "as is" without any express or
            implied warranty or guarantee from the Contest Entities (as defined
            in the Official Rules), including but not limited to, the implied
            warranties of merchantability, fitness for a particular purpose and
            non-infringement to the extent permitted by law.
          </p>
          <p>
            {/* REVIEW: §14 verbatim except — dropped "lost, late, or
                misdirected entries" and "(including winner selection and
                prizes)" as Contest-mechanical; "completion as planned" →
                "operating as planned"; added "suspend" alongside cancel/modify/
                terminate; scoped to access to/use of the Site. */}
            The Contest Entities are not responsible for telecommunication,
            internet, or hardware or software failures of any kind in connection
            with your access to or use of the Site, including by reason of any
            bug or computer virus or other failure. The Contest Entities may
            cancel, modify, suspend, or terminate the Site (or any part of it)
            without prior notice if it is not capable of operating as planned,
            including by reason of infection of computer virus, tampering,
            unauthorized intervention, force majeure, or technical difficulties
            of any kind.
          </p>
        </Section>

        <Section id="indemnification" title="7. Indemnification and Release of Liability">
          {/* Source: Official Rules §12, re-scoped. Defined terms ("Contest
              Entities", "Released Parties") carry the meanings given in the
              Official Rules. */}
          <p>
            {/* REVIEW: §12 verbatim except — "BY PARTICIPATING" → "BY ACCESSING
                OR USING THE SITE"; the entry/prize/Broadcast/participation scope
                list → "YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE,
                THE SITE". */}
            BY ACCESSING OR USING THE SITE, YOU AGREE TO THE FULLEST EXTENT
            PERMITTED BY LAW TO RELEASE, DISCHARGE AND HOLD HARMLESS TWITCH,
            YOUTUBE, SECRETLABS, THE CONTEST ENTITIES AND THEIR RESPECTIVE
            PARENT, AFFILIATES AND SUBSIDIARY COMPANIES, ADVERTISING AND
            PROMOTION AGENCIES, AND THEIR RESPECTIVE OFFICERS, MANAGERS,
            DIRECTORS, EMPLOYEES, INDEPENDENT CONTRACTORS, REPRESENTATIVES AND
            AGENTS ("RELEASED PARTIES") FROM AND AGAINST ANY AND ALL ALLEGED OR
            ACTUAL CLAIMS, CAUSES OF ACTION, DEMANDS, LOSSES, SETTLEMENTS,
            LIABILITIES AND DAMAGES OF ANY KIND WHATSOEVER EXISTING NOW OR
            ARISING IN THE FUTURE (INCLUDING, WITHOUT LIMITATION, BODILY INJURY,
            PERSONAL INJURY, DEATH, DISABILITY AND PROPERTY DAMAGE, VIOLATION OF
            PROPRIETARY, PUBLICITY, PRIVACY OR ANY OTHER RIGHT), COSTS AND
            EXPENSES (INCLUDING, WITHOUT LIMITATION, REASONABLE ATTORNEYS' FEES,
            COURT COSTS, SETTLEMENT AND DISBURSEMENTS) DIRECTLY OR INDIRECTLY
            ARISING OUT OF YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR
            USE, THE SITE IN WHOLE OR IN PART.
          </p>
          <p>
            {/* REVIEW: §12 verbatim except — "By entering the Contest or
                accepting a prize" → "By accessing or using the Site"; "these
                Official Rules" → "these Terms"; "EVERY PARTICIPANT" → "EVERY
                USER". */}
            By accessing or using the Site, you covenant to the fullest extent
            permitted by law not to sue any Released Party or cause them to be
            sued regarding any matter released above, and not to disaffirm, limit
            or rescind this release. A waiver by one or more of the Contest
            Entities of any term in these Terms does not constitute a waiver of
            any other provision. SOME JURISDICTIONS DO NOT ALLOW CERTAIN
            LIMITATIONS OR EXCLUSION OF LIABILITY, SO THE FOREGOING MAY NOT APPLY
            TO EVERY USER. Any provision adjudged to be invalid shall be struck
            from these Terms and the remainder shall continue in full force and
            effect.
          </p>
        </Section>

        <Section id="dispute-resolution" title="8. Alternative Dispute Resolution; Arbitration">
          {/* Source: Official Rules §15, re-scoped. Replaces the prior Illinois
              governing-law/venue section so the Terms adopt the same forum the
              Rules use, rather than a divergent one. */}
          <p>
            {/* REVIEW: §15 verbatim except — "participants" → "users"; "the
                terms of these Official Rules" → "these Terms"; "connected with
                the Contest or the prizes awarded" → "connected with the Site or
                your access to or use of the Site". LA venue, AAA, the 30/10-day
                process, the class + jury waivers, and the CA/NJ/NY carve-out are
                unchanged. */}
            To the extent permitted by law and excluding any users residing or
            working within the States of California, New Jersey or New York, any
            disputes relating to these Terms or the breach thereof, or connected
            with the Site or your access to or use of the Site, shall be
            submitted to binding arbitration in Los Angeles, CA in accordance
            with the Commercial Arbitration rules of the American Arbitration
            Association (the "AAA"). Each party agrees that any dispute
            resolution proceedings will be conducted only on an individual basis
            and not in a class, consolidated or representative action. Each party
            hereby waives the right to participate in any class action, whether
            in court or arbitration. In the event that either party desires to
            arbitrate any such dispute, such party shall so notify the other
            party and the parties shall endeavor in good faith, for a period of
            30 days, to resolve such dispute without arbitration. In the event
            that the parties cannot resolve the dispute within such 30 day
            period, then within 10 days thereafter, the parties shall jointly
            designate a single arbitrator to hear the dispute, or, if the parties
            are unable to jointly select an arbitrator, an arbitrator shall be
            chosen according to the rules of the AAA. The arbitration, including
            the rendering of the award, shall take place in Los Angeles, CA and
            shall be the exclusive forum for resolving such dispute, controversy,
            or claim. The decision of the arbitrator shall be final and binding
            upon the parties hereto. The decision of the arbitrator shall be
            executory, and judgment thereon may be entered by any court of
            competent jurisdiction. If for any reason a claim proceeds in court
            rather than in arbitration, each party waives any right to a jury
            trial, excluding in any civil matters brought within the States of
            California, New Jersey, or New York.
          </p>
        </Section>

        <Section id="changes" title="9. Changes to these terms">
          <p>
            We may update these Terms from time to time. When we make
            material changes that significantly affect your rights and
            obligations, we will update the version number and "Last
            updated" date at the top of this page. If you have an account,
            we may also ask you to acknowledge the new version before you
            continue to use the Site. Your continued use after the
            effective date of any update means you accept the updated
            Terms.
          </p>
        </Section>

        <Section id="general" title="10. General">
          <p>
            Our failure to enforce any provision of these Terms is not a
            waiver of that provision. These Terms, together with the{' '}
            <a href="/rules" className="text-brand-redAccent underline-offset-2 hover:underline">
              Official Rules
            </a>{' '}
            and the{' '}
            <a href="/privacy" className="text-brand-redAccent underline-offset-2 hover:underline">
              Privacy Notice
            </a>
            , are the entire agreement between you and us regarding the
            Site.
          </p>
        </Section>

        <Section id="contact" title="11. Contact us">
          <p>
            Questions about these Terms?
          </p>
          <p className="pl-4 text-white/75">
            Infinity Marketing Team, LLC
            <br />
            Attention: Legal
            <br />
            <a
              href={`mailto:${SUPPORT_CONTACT}`}
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              {SUPPORT_CONTACT}
            </a>
          </p>
        </Section>
      </div>
    </article>
  );
}

/**
 * Lead intro shown at the top of the Terms. Establishes the Official Rules as
 * the controlling document for the contest and defines the "Terms" / "Site"
 * terms used throughout. Wording is the legal-reviewed 2026-06-26 copy. The body
 * sections (1) drop the contest-mechanical material the Rules own and (2) mirror
 * the Rules' liability/disclaimer/dispute provisions re-scoped from "by
 * participating" to "by accessing or using the Site" so those protections reach
 * every visitor, not just entrants — see §§6-8 and their inline notes.
 */
function RulesAuthorityNotice() {
  return (
    <div
      role="note"
      className="mt-8 rounded-md border-l-4 border-brand-redAccent bg-white/[0.04] px-5 py-4 text-[15px] leading-relaxed text-white/80"
    >
      The{' '}
      <a href="/rules" className="text-brand-redAccent underline-offset-2 hover:underline">
        Official Rules
      </a>{' '}
      are the primary authority governing participation in the State Farm
      Gamerhood Season 5 contest. These Terms of Use ("Terms") govern your
      access to and use of the State Farm Gamerhood Season 5 website (the "Site")
      and game. In case of a conflict between these Terms and provisions in the
      Official Rules concerning the contest itself, the Official Rules
      control.
    </div>
  );
}

type SectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-heading text-lg uppercase tracking-wider text-white">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[15px] text-white/80">{children}</div>
    </section>
  );
}

